// React and related imports
import { useEffect, useState } from 'react';

// Redux selectors, hooks, and actions
import { useAppSelector } from '../redux/hooks/useAppSelector';
import {
  selectIsContentModalOpen,
  selectSelectedContent,
} from '../redux/selectors/contentSelectors';

// Types and Interfaces
import { Status } from '../common/enums/Status';
import { IMultiLanguageValue } from '../common/interfaces/MultiLanguageValue.interface';
import { ContentType } from '../common/enums/ContentTypes';
import { INotificationInfo } from '../common/interfaces/NotificationInfo.interface';
import { IMedia } from '../common/interfaces/Media.interface';
import { IQuestion } from '../common/interfaces/Question.interface';
import { IContentWithContextType } from '../common/interfaces/ContentWithContextType';

export type ContentState = IContentWithContextType | null;

interface UseContentStateReturn {
  isModalOpen: boolean;
  contentState: ContentState;
  updateField: (
    field: keyof IContentWithContextType,
    value:
      | null
      | string
      | ContentType
      | Status
      | IMultiLanguageValue[]
      | INotificationInfo
      | IMedia
      | IQuestion[]
  ) => void;
}

export const useContentState = (): UseContentStateReturn => {
  const isModalOpen = useAppSelector(selectIsContentModalOpen);
  const selectedContent = useAppSelector(selectSelectedContent);

  const [contentState, setContentState] = useState<ContentState>(null);

  useEffect(() => {
    // Update the content state whenever selectedContent changes
    setContentState(selectedContent);
  }, [selectedContent]);

  const updateField = (
    field: keyof IContentWithContextType | string, // Allow nested keys as strings
    value:
      | null
      | string
      | ContentType
      | Status
      | IMultiLanguageValue[]
      | INotificationInfo
      | IMedia
      | IQuestion[]
  ) => {
    setContentState((prev) => {
      if (!prev) return null;

      // Handle nested fields
      if (field.includes('[')) {
        const [outerKey, innerKey] = field.split(/\[|\]/).filter(Boolean); // Extract outer and inner keys
        return {
          ...prev,
          [outerKey]: {
            ...(prev[outerKey as keyof IContentWithContextType] as object), // Shallow copy of the nested object
            [innerKey]: value, // Update the specific inner key
          },
        };
      }

      // Handle flat fields
      return {
        ...prev,
        [field as keyof IContentWithContextType]: value,
      };
    });
  };

  return { isModalOpen, contentState, updateField };
};
